import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@powersync/react";
import { allowedTransitions, type NeedState, type Role } from "@ect/contracts";
import { q, setNeedState, formatDate } from "@ect/core";
import { Button, Field, FormSheet, NeedStateChip, Textarea, Timeline } from "@ect/ui";
import { useSession } from "../app/session";
import { useSync } from "../app/powersync";

interface Ev { id: string; from_state: string | null; to_state: NeedState; comment: string | null; device_ts: string }

/** Need detail (sprint 6.3): status timeline + the actions the role may take (chef validation queue). */
export function NeedDetail({ need, onClose }: { need: { id: string; state: NeedState; title: string; qty: number; unit: string | null } | null; onClose: () => void }) {
  const { t } = useTranslation();
  const { session, role } = useSession();
  const { db } = useSync();
  const { data: events } = useQuery<Ev>(q.needHistory(need?.id ?? "").sql, q.needHistory(need?.id ?? "").params);
  const [comment, setComment] = useState("");
  const [pendingReject, setPendingReject] = useState(false);
  const userRoles = (session?.user.roles.map((r) => r.role as Role) ?? [role as Role]);
  const actions = need ? allowedTransitions(need.state, userRoles).filter((a) => !["in_stock", "ordered", "deferred"].includes(a.to)) : [];
  const act = async (to: NeedState, requiresComment?: boolean) => {
    if (!need) return;
    if (requiresComment && !comment.trim()) { setPendingReject(true); return; }
    await setNeedState(db, { needId: need.id, state: to, comment: comment.trim() || undefined });
    setComment(""); setPendingReject(false); onClose();
  };
  const label: Record<string, string> = { validated_by_chef: t("needDetail.validate"), rejected: t("needDetail.reject"), sent_to_purchasing: t("needDetail.sendToPurchasing"), delivered_to_site: t("needDetail.deliver"), closed: t("needDetail.close") };
  return (
    <FormSheet open={!!need} onOpenChange={(o) => !o && onClose()} title={need?.title ?? ""} description={need ? `${need.qty} ${need.unit ?? ""}` : undefined} footer={actions.length ? <>{actions.map((a) => <Button key={a.to} variant={a.to === "rejected" ? "danger" : "primary"} onClick={() => void act(a.to, a.requiresComment)}>{label[a.to] ?? a.to}</Button>)}</> : undefined}>
      <div className="grid gap-4">
        {need && <NeedStateChip state={need.state} label={t(`needState.${need.state}`)} />}
        <Timeline items={events.map((e) => ({ id: e.id, title: t(`needState.${e.to_state}`), meta: `${formatDate(new Date(e.device_ts))}${e.comment ? ` · ${e.comment}` : ""}`, tone: e.to_state === "rejected" ? "critical" : e.to_state === "closed" || e.to_state === "delivered_to_site" ? "good" : "neutral" }))} />
        {(pendingReject || actions.some((a) => a.requiresComment)) && <Field id="need-comment" label={t("needDetail.reason")} error={pendingReject && !comment.trim() ? t("chantier.commentRequired") : undefined}><Textarea id="need-comment" value={comment} onChange={(e) => setComment(e.target.value)} /></Field>}
      </div>
    </FormSheet>
  );
}
